import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    setDoc,
    writeBatch,
    where,
} from "firebase/firestore";
import { db } from "../firebase";

const COLLECTION_NAME = "electronicBookmarks";
export const MAX_ELECTRONIC_BOOKMARK_GROUP_NAME_LENGTH = 32;
export const ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR =
    "group-name-too-long";
export const ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT =
    `Nazwa grupy może mieć maksymalnie ${MAX_ELECTRONIC_BOOKMARK_GROUP_NAME_LENGTH} znaki.`;

export const normalizeGroupName = (groupName: string) =>
    groupName.trim().replace(/\s+/g, " ").toLowerCase();

export const isElectronicBookmarkGroupNameTooLong = (groupName: string) =>
    groupName.length > MAX_ELECTRONIC_BOOKMARK_GROUP_NAME_LENGTH;

export const createElectronicBookmarkId = (
    clientId: string,
    groupName: string,
    bookId: string,
) => {
    const normalizedGroupName = normalizeGroupName(groupName);
    return `${clientId}__${normalizedGroupName}__${bookId}`;
};

export const getClientElectronicBookmarks = async (
    clientId: string,
): Promise<ElectronicBookmark[]> => {
    const q = query(
        collection(db, COLLECTION_NAME),
        where("clientId", "==", clientId),
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as Omit<ElectronicBookmark, "id">;
        return {
            id: docSnap.id,
            ...data,
        };
    });
};

export const getElectronicBookmark = async (
    clientId: string,
    groupName: string,
    bookId: string,
): Promise<ElectronicBookmark | null> => {
    const bookmarkId = createElectronicBookmarkId(clientId, groupName, bookId);
    const bookmarkRef = doc(db, COLLECTION_NAME, bookmarkId);
    const snapshot = await getDoc(bookmarkRef);

    if (!snapshot.exists()) return null;

    const data = snapshot.data() as Omit<ElectronicBookmark, "id">;
    return {
        id: snapshot.id,
        ...data,
    };
};

export const upsertElectronicBookmark = async (params: {
    clientId: string;
    groupName: string;
    bookId: string;
    pageNum: number | null;
    updatedBy?: string;
}): Promise<ElectronicBookmark> => {
    const trimmedGroupName = params.groupName.trim().replace(/\s+/g, " ");
    const groupNameNormalized = normalizeGroupName(trimmedGroupName);
    const id = createElectronicBookmarkId(
        params.clientId,
        trimmedGroupName,
        params.bookId,
    );

    const payload: ElectronicBookmark = {
        id,
        clientId: params.clientId,
        groupName: trimmedGroupName,
        groupNameNormalized,
        bookId: params.bookId,
        pageNum: params.pageNum,
        updatedAt: Date.now(),
        updatedBy: params.updatedBy,
    };

    await setDoc(doc(db, COLLECTION_NAME, id), payload);

    return payload;
};

export const renameElectronicBookmark = async (params: {
    bookmark: ElectronicBookmark;
    nextGroupName: string;
    updatedBy?: string;
}): Promise<ElectronicBookmark> => {
    const trimmedGroupName = params.nextGroupName.trim().replace(/\s+/g, " ");

    if (!trimmedGroupName) {
        throw new Error("invalid-group-name");
    }

    if (trimmedGroupName.length > MAX_ELECTRONIC_BOOKMARK_GROUP_NAME_LENGTH) {
        throw new Error(ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR);
    }

    const groupNameNormalized = normalizeGroupName(trimmedGroupName);
    const nextId = createElectronicBookmarkId(
        params.bookmark.clientId,
        trimmedGroupName,
        params.bookmark.bookId,
    );

    const payload: ElectronicBookmark = {
        ...params.bookmark,
        id: nextId,
        groupName: trimmedGroupName,
        groupNameNormalized,
        updatedAt: Date.now(),
        updatedBy: params.updatedBy,
    };

    if (nextId === params.bookmark.id) {
        await setDoc(doc(db, COLLECTION_NAME, nextId), payload);
        return payload;
    }

    const nextBookmarkRef = doc(db, COLLECTION_NAME, nextId);
    const existingBookmarkSnapshot = await getDoc(nextBookmarkRef);

    if (existingBookmarkSnapshot.exists()) {
        throw new Error("group-already-exists");
    }

    const previousBookmarkRef = doc(db, COLLECTION_NAME, params.bookmark.id);
    const batch = writeBatch(db);
    batch.set(nextBookmarkRef, payload);
    batch.delete(previousBookmarkRef);
    await batch.commit();

    return payload;
};

export const deleteElectronicBookmark = async (bookmarkId: string) => {
    const bookmarkRef = doc(db, COLLECTION_NAME, bookmarkId);
    await deleteDoc(bookmarkRef);
};
